/**
 * Inbox row and batch view model (PROD-3274, DF-04): turns an `InboxDocument` into the words, chips and actions of the
 * F1 table. Pure, so the table component only draws it. Copy follows the canvas IntakeInbox (14 Oct) and contract §3.
 *
 * An enum value this file does not know (the server may add values) shows as it is and offers no action.
 */
import { IntakeApiError } from '../api/intake-client';
import type { InboxDocument, IntakeDocType } from '../types';

export type ChipSemantic = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface Chip {
  label: string;
  semantic: ChipSemantic;
}

export type RowAction =
  | { kind: 'review' }
  | { kind: 'confirm' }
  | { kind: 'add'; label: string }
  | { kind: 'retry' }
  | { kind: 'disregard' }
  | { kind: 'wait' };

export interface RowView {
  /** "Recognized as": the label and an optional sub-line. */
  recognized: { label: string; sub: string | null; reading: boolean };
  identity: Chip | null;
  goesTo: { label: string; sub: string | null } | null;
  status: Chip | null;
  actions: RowAction[];
  /** The whole row takes the danger surface (identity Conflict). */
  held: boolean;
  /** The type picker applies: the user may change the type before Add. */
  picker: boolean;
}

/** Labels for the types the inbox offers. Any other value the server returns is shown as it is. */
export const DOC_TYPE_LABEL: Record<string, string> = {
  lab_report: 'Lab report',
  other: 'Other (filed, not read)',
};

/**
 * The types a person may pick (scope, Samer 18 Oct: labs, omics, genomics; `other` is filed, not read). Omics and
 * genomic reports join this list once the server accepts `omics_report` / `genomic_report`.
 */
export const DOC_TYPE_ORDER: IntakeDocType[] = ['lab_report', 'other'];

export const isOfferedType = (t: string | null | undefined): t is IntakeDocType =>
  !!t && (DOC_TYPE_ORDER as string[]).includes(t);

/** The five visible steps, in server order, with the canvas labels ("Classify" reads as "Place"). */
export const STEP_ORDER = ['Recognize', 'Read', 'Classify', 'Verify', 'Present'] as const;
export const STEP_LABEL: Record<string, string> = {
  Recognize: 'Recognize',
  Read: 'Read',
  Classify: 'Place',
  Verify: 'Verify',
  Present: 'Review and add',
};

const KNOWN_STATUS = ['Queued', 'Running', 'AwaitingReview', 'Completed', 'Failed', 'Discarded'];
const KNOWN_IDENTITY = ['Verified', 'NeedsIdentityReview', 'Conflict', 'NotChecked'];

/** A&P's DF-02 review route. */
export const intakeReviewPath = (patientId: string, runId: string) => `/patients/edit/${patientId}/intake/${runId}`;

/** The patient chart's Documents section, where the inbox is. */
export const intakeInboxPath = (patientId: string) => `/patients/edit/${patientId}?tab=chart&inner=documents`;

export const isInFlight = (d: InboxDocument) => d.status === 'Queued' || d.status === 'Running';

export const docTypeLabel = (t: string | null) => (t === null ? '' : (DOC_TYPE_LABEL[t] ?? t));

/** "PDF · 2 pages", "phone photo", "image · 1 page". The page count is left out while it is null. */
export function fileSubLine(d: InboxDocument): string {
  const pages = d.pageCount === null ? null : `${d.pageCount} ${d.pageCount === 1 ? 'page' : 'pages'}`;
  const ct = d.contentType.toLowerCase();
  if (ct.startsWith('image/')) return d.docType === 'clinical_photo' || !pages ? 'phone photo' : `phone photo · ${pages}`;
  const kind = ct === 'application/pdf' ? 'PDF' : ct;
  return pages ? `${kind} · ${pages}` : kind;
}

export const isImage = (d: InboxDocument) => d.contentType.toLowerCase().startsWith('image/');

function identityChip(d: InboxDocument): Chip | null {
  switch (d.identityStatus) {
    case 'Verified':
      return { label: '✓ Name and DOB match', semantic: 'success' };
    case 'NeedsIdentityReview':
      return { label: 'Confirm patient', semantic: 'warning' };
    case 'Conflict':
      return { label: 'Conflict · held', semantic: 'error' };
    case 'NotChecked':
      if (d.docType === 'clinical_photo') return { label: 'Photos carry no ID · you confirm', semantic: 'neutral' };
      // Still being recognized: nothing to say about the check yet.
      return d.docType === null && isInFlight(d) ? null : { label: 'Not checked', semantic: 'neutral' };
    default:
      return { label: d.identityStatus, semantic: 'neutral' };
  }
}

function goesTo(d: InboxDocument): RowView['goesTo'] {
  if (d.identityStatus === 'Conflict') return { label: 'Nowhere until resolved', sub: 'not read further' };
  switch (d.target) {
    case 'LabReport': {
      const n = d.rowCounts?.total;
      return { label: n === undefined || n === null ? 'Labs' : `Labs · ${n} ${n === 1 ? 'value' : 'values'}`, sub: 'own ranges kept' };
    }
    case 'PatientDocument':
      return { label: 'Documents', sub: null };
    case 'Media':
      return { label: 'Media', sub: 'filed only, never interpreted' };
    case null:
      if (isInFlight(d) || d.status === 'Failed' || d.docType === null) return null;
      return { label: 'Not filed unless you add it', sub: null };
    default:
      return { label: d.target, sub: null };
  }
}

function statusChip(d: InboxDocument): Chip | null {
  if (d.identityStatus === 'Conflict') return { label: 'Held', semantic: 'error' };
  if (d.identityStatus === 'NeedsIdentityReview') return { label: 'Confirm patient', semantic: 'warning' };
  switch (d.status) {
    case 'Queued':
    case 'Running':
      return { label: 'Reading', semantic: 'neutral' };
    case 'Failed':
      return { label: 'Could not read', semantic: 'error' };
    case 'AwaitingReview':
      if (d.docType === 'lab_report') return { label: 'Ready for review', semantic: 'info' };
      return d.target === null ? { label: 'Your choice', semantic: 'neutral' } : { label: 'Ready to file', semantic: 'info' };
    case 'Completed':
      return { label: 'Added', semantic: 'success' };
    case 'Discarded':
      return { label: 'Disregarded', semantic: 'neutral' };
    default:
      return { label: d.status, semantic: 'neutral' };
  }
}

export function addLabel(docType: string | null): string {
  if (docType === 'other') return 'Add as document';
  return 'Add';
}

function actionsFor(d: InboxDocument): RowAction[] {
  // An unknown status or identity value: show it as it is, offer nothing.
  if (!KNOWN_STATUS.includes(d.status) || !KNOWN_IDENTITY.includes(d.identityStatus)) return [];
  if (d.identityStatus === 'Conflict') return [{ kind: 'disregard' }];
  if (d.identityStatus === 'NeedsIdentityReview') {
    return d.runId ? [{ kind: 'confirm' }, { kind: 'disregard' }] : [{ kind: 'disregard' }];
  }
  switch (d.status) {
    case 'Queued':
    case 'Running':
      return [{ kind: 'wait' }];
    case 'Failed':
      return d.runId ? [{ kind: 'retry' }, { kind: 'disregard' }] : [{ kind: 'disregard' }];
    case 'AwaitingReview':
      if (d.docType === 'lab_report') {
        return d.runId ? [{ kind: 'review' }, { kind: 'disregard' }] : [{ kind: 'disregard' }];
      }
      return [{ kind: 'add', label: addLabel(d.docType) }, { kind: 'disregard' }];
    default:
      return [];
  }
}

export function rowView(d: InboxDocument): RowView {
  const reading = d.docType === null && isInFlight(d);
  const stepNo = STEP_ORDER.indexOf(d.step as (typeof STEP_ORDER)[number]) + 1;
  const actions = actionsFor(d);
  return {
    recognized: reading
      ? {
          label: 'Reading…',
          sub: d.status === 'Running' && stepNo > 0 ? `step ${stepNo} of 5 · ${STEP_LABEL[d.step]}` : null,
          reading: true,
        }
      : { label: d.docType === null ? '—' : docTypeLabel(d.docType), sub: null, reading: false },
    identity: identityChip(d),
    goesTo: goesTo(d),
    status: statusChip(d),
    actions,
    held: d.identityStatus === 'Conflict',
    picker: actions.some((a) => a.kind === 'add'),
  };
}

// ---- Type picker (PROD-3339) ------------------------------------------------------------------------------------

export interface TypePickerView {
  /** What the picker shows first: the stored type, else the suggestion, else nothing (the person must pick). */
  initial: IntakeDocType | null;
  /** The classifier's suggestion when it is offered here, for the "Suggested" mark; null when the row has a type. */
  suggested: IntakeDocType | null;
  /** The stored type (null while untyped). Confirming it again changes nothing. */
  current: string | null;
}

/**
 * Whether the row offers the type picker, and what it starts on: every open row with a run that is not being read or held.
 * A suggestion outside the offered types (labs scope) is not pre-selected.
 */
export function typePickerFor(d: InboxDocument): TypePickerView | null {
  if (d.inboxStatus !== 'Open' || d.runId === null) return null;
  if (isInFlight(d) || d.identityStatus === 'Conflict') return null;
  if (d.status !== 'AwaitingReview' && d.status !== 'Failed') return null;
  const suggested = d.docType === null && isOfferedType(d.suggestedDocType) ? d.suggestedDocType : null;
  return { initial: isOfferedType(d.docType) ? d.docType : suggested, suggested, current: d.docType };
}

/**
 * What Confirm type does with a picked type. `add`: the inbox Add with that type files it (source User). `read`: a lab
 * report: the Add saves the pick and answers review_required, then the reading starts again (Retry) when the run has
 * no values yet, or review opens. `none`: nothing changed.
 */
export function confirmTypeAction(d: InboxDocument, picked: string | null): 'add' | 'read' | 'none' {
  if (picked === null || picked === d.docType) return 'none';
  return picked === 'lab_report' ? 'read' : 'add';
}

/** A lab report pick on a run with no values yet re-reads the file (Patient #1207 Retry); with values, review opens. */
export const labPickNeedsReading = (d: InboxDocument) => (d.rowCounts?.total ?? 0) === 0;

// ---- Batches ----------------------------------------------------------------------------------------------------

export interface BatchView {
  batchId: string;
  documents: InboxDocument[];
}

/** Groups by `batchId`, newest batch first (by the latest `uploadedAt` inside it); file order inside a batch is kept. */
export function groupByBatch(docs: InboxDocument[]): BatchView[] {
  const map = new Map<string, InboxDocument[]>();
  for (const d of docs) map.set(d.batchId, [...(map.get(d.batchId) ?? []), d]);
  const latest = (list: InboxDocument[]) => Math.max(...list.map((x) => Date.parse(x.uploadedAt) || 0));
  return [...map.entries()]
    .map(([batchId, documents]) => ({ batchId, documents }))
    .sort((a, b) => latest(b.documents) - latest(a.documents));
}

/** "Batch of 9 files · dropped 14 Oct 2026, 10:12 by Rania Saeed". No name when `displayName` is null. */
export function batchHeading(docs: InboxDocument[]): string {
  const first = docs[0];
  const when = new Date(first.uploadedAt);
  const date = Number.isNaN(when.getTime())
    ? ''
    : `${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  const name = first.uploadedBy?.displayName;
  const count = `Batch of ${docs.length} ${docs.length === 1 ? 'file' : 'files'}`;
  const dropped = date ? `dropped ${date}` : 'dropped';
  return `${count} · ${dropped}${name ? ` by ${name}` : ''}`;
}

export interface StepView {
  key: string;
  title: string;
  /** Null when the data does not support a count: the step is drawn plain. */
  value: string | null;
  description: string;
}

/** Has this document finished the step at `index` (0 = Recognize)? Settled runs finished all five. */
function passed(d: InboxDocument, index: number): boolean {
  if (d.status === 'AwaitingReview' || d.status === 'Completed') return true;
  const at = STEP_ORDER.indexOf(d.step as (typeof STEP_ORDER)[number]);
  return at > index;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The five-step strip of a batch, counted only from each document's `status`, `step`, `docType`, `target`. */
export function stepStrip(docs: InboxDocument[]): StepView[] {
  const total = docs.length;
  const held = docs.filter((d) => d.identityStatus === 'Conflict').length;
  const recognized = docs.filter((d) => d.docType !== null || passed(d, 0)).length;
  const read = docs.filter((d) => d.docType !== 'clinical_photo' && passed(d, 1)).length;
  const lab = docs.filter((d) => d.target === 'LabReport').length;
  const letters = docs.filter((d) => d.target === 'PatientDocument').length;
  const media = docs.filter((d) => d.target === 'Media').length;
  const placed = [lab && `${lab} lab`, letters && plural(letters, 'document', 'documents'), media && `${media} media`].filter(Boolean);
  const checked = docs.filter((d) => d.docType !== 'clinical_photo' && passed(d, 3)).length;
  const waiting = docs.filter((d) => d.status === 'AwaitingReview' && d.identityStatus !== 'Conflict').length;
  return [
    {
      key: 'Recognize',
      title: '1 Recognize',
      value: `${recognized} of ${total}${held ? ` · ${held} held` : ''}`,
      description: 'what each file is · patient check',
    },
    { key: 'Read', title: '2 Read', value: plural(read, 'document', 'documents'), description: 'photos skip reading' },
    {
      key: 'Classify',
      title: '3 Place',
      value: placed.length ? placed.join(', ') : null,
      description: 'which part of the record',
    },
    { key: 'Verify', title: '4 Verify', value: `${checked} checked`, description: 'values traced to the page' },
    { key: 'Present', title: '5 Review and add', value: `${waiting} waiting`, description: 'Add or Disregard' },
  ];
}

/** The inbox action whose failure is being shown. */
export type InboxAction = 'add' | 'confirm' | 'retry' | 'disregard';

/**
 * The line under a row (Add, This is <name>, Try again) or in the Disregard dialog when the action fails (contract v0.16).
 * Each code the inbox actions can answer gets words the user can act on; anything else keeps the plain "try again".
 */
export function inboxActionError(error: unknown, action: InboxAction): string {
  const fallback = action === 'disregard' ? 'The file could not be disregarded. Try again.' : 'That did not work. Try again.';
  if (!(error instanceof IntakeApiError)) return fallback;
  switch (error.code) {
    case 'tenant_required':
      return 'Your sign-in has no organization. Sign out and in again, then try again.';
    case 'user_required':
      return 'Your sign-in has no user. Sign out and in again, then try again.';
    case 'run_in_progress':
      return 'This file is still being read. Try again when it has finished.';
    case 'confirm_in_progress':
      // Contract v0.20 (#1116): a lab Confirm has claimed the run and is writing the results; Add and Disregard wait for it.
      return 'This report is being added to the record. Refresh the inbox in a moment.';
    case 'not_open':
      // Disregard: the file was added, or an earlier Add already filed it (Add finishes it). Add: it was already added.
      // This is <name> and Try again: the file left the inbox some other way, so the words say no more than that.
      if (action === 'disregard') return "This file is already in the patient's documents. Refresh the inbox, or add it to finish.";
      if (action === 'add') return 'This file was already added. Refresh the inbox.';
      return 'This file is no longer open in the inbox. Refresh the inbox.';
    case 'document_not_found':
      return 'This file is no longer in the inbox. Refresh the inbox.';
    default:
      return fallback;
  }
}
